/**
 * Portal module API client — fetchApi wrapper for admin-side portal endpoints.
 *
 * Phase 5 of demo-tenant architecture (2026-05-06): both production and demo
 * mode route to /v1/<path>. The difference is which (token, workspaceId) pair
 * fetchApi attaches — demo uses the ephemeral demo-tenant credentials minted
 * by /v1/auth/demo-bootstrap. There are no local Next.js mock routes for this
 * module and no DemoModeReadOnlyError gates.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IMPORTANT AUTH SPLIT — do NOT merge these two paths:
 *
 *   Admin-side portal management endpoints
 *     /portal-settings  /portal-themes  /portal-news  /portal/auth/direct-login
 *     /employee-suppressions  /team-portal-settings  /team-portal
 *   → use portalAdminFetch (this file) — carries ADMIN JWT via fetchApi
 *
 *   Employee-facing portal endpoints (PortalJwtGuard-protected)
 *     /portal/enrollments  /portal/certificates  /portal/gamification-*
 *     /portal/badges  /portal/leaderboard  /portal/points  /portal/challenges
 *     /portal/news  /portal/team/*
 *   → use fetchPortalApi (lib/api/portal-fetch.ts) — carries PORTAL JWT
 *     (stored under hf-portal:token)
 *
 *   Portal SSO/SAML/refresh endpoints (@Public, no auth header needed)
 *     /portal/auth/sso/initiate  /portal/auth/sso/callback
 *     /portal/auth/saml/initiate  /portal/auth/refresh
 *   → use raw fetch pointing at API_CONFIG.baseUrl directly (bypass fetchApi)
 *
 * These three paths must never be merged — using the wrong JWT causes 401s
 * that the fetchApi 401-handler misinterprets as an expired admin session,
 * wiping admin localStorage and force-redirecting to /login.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { fetchApi, ApiClientError } from '@/lib/api/client';

export { ApiClientError as PortalApiClientError };

/**
 * Admin-side portal fetch wrapper.
 *
 * Pass the path WITHOUT a /v1/ or /api/ prefix — this wrapper enforces the
 * correct module prefix so consumers cannot accidentally double-prefix.
 *
 * Examples (admin-side):
 *   portalAdminFetch('portal-settings')          → /v1/portal-settings
 *   portalAdminFetch('portal-themes/abc123')     → /v1/portal-themes/abc123
 *   portalAdminFetch('employee-suppressions')    → /v1/employee-suppressions
 *   portalAdminFetch('team-portal-settings')     → /v1/team-portal-settings
 *   portalAdminFetch('team-portal/managers')     → /v1/team-portal/managers
 */
export async function portalAdminFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  const endpoint = `/${cleanPath}`;
  return fetchApi<T>(endpoint, options as RequestInit & { skipAuth?: boolean });
}
